import React, { useMemo } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { BENCH_METRICS, CHECKPOINT_DAYS, checkpointRows } from './launchCurves';
import { Multiple, metricFormatters } from './BenchmarkMultiple';
import { SERIES } from './LaunchCurveChart';

const CELL = 'px-2 py-1.5';

// A coloured dot matching the chart line of the benchmark group it labels.
function Dot({ color }) {
  return <span className="inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle" style={{ backgroundColor: color }} />;
}

// One checkpoint cell: the game's total up to that day and, on the dot lines,
// its multiples of the normal-release and other-pre-release averages ('not
// reached' before the game has had that many full days).
function CheckpointCell({ cell, fmt }) {
  if (cell.value == null) {
    return <TableCell className={`${CELL} text-right text-[11px] text-ink-mute`}>not reached</TableCell>;
  }
  return (
    <TableCell className={`${CELL} text-right align-top`}>
      <p className="text-xs font-bold text-ink whitespace-nowrap">{fmt(cell.value)}</p>
      <p className="text-[11px] whitespace-nowrap">
        <Dot color={SERIES.normal.color} />
        <Multiple value={cell.vsNormal} group={cell.normal} fmt={fmt} age={cell.day} />
      </p>
      <p className="text-[11px] whitespace-nowrap">
        <Dot color={SERIES.pre.color} />
        <Multiple value={cell.vsPeers} group={cell.peers} fmt={fmt} age={cell.day} />
      </p>
    </TableCell>
  );
}

// Compact totals since release for the selected game at fixed days, for every
// metric, against the same benchmark groups as the chart and scorecard — sized
// to sit next to the chart.
export default function CheckpointTable({ curves, selected, benchmark }) {
  const rows = useMemo(
    () => checkpointRows(curves, selected, { checkpoints: CHECKPOINT_DAYS, ...benchmark }),
    [curves, selected, benchmark],
  );

  return (
    <div>
      <p className="text-[11px] text-ink-dim mb-1">
        Total since release · <Dot color={SERIES.normal.color} />vs normal releases · <Dot color={SERIES.pre.color} />vs other pre-releases
      </p>
      <div className="border border-white/10 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={CELL} />
              {CHECKPOINT_DAYS.map(d => <TableHead key={d} className={`${CELL} text-right whitespace-nowrap`}>Day {d}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => {
              const { fmt } = metricFormatters(row.metric);
              return (
                <TableRow key={row.metric}>
                  <TableCell className={`${CELL} text-[11px] font-bold text-ink align-top`}>
                    {BENCH_METRICS.find(m => m.key === row.metric).label}
                  </TableCell>
                  {row.cells.map(cell => <CheckpointCell key={cell.day} cell={cell} fmt={fmt} />)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
